import { Box } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";
import {
  IAction,
  IField,
  IPageable,
  ISort,
  Params,
} from "../../../types/table/interfaces.type";
import PaginatedTable from "./PaginatedTable";

interface ISearchableTableProps<T> {
  filters: Params<T>;
  fields: IField<T>[];
  actions?: IAction<T>[];
  fetchFunc:
    | ((params: Params<T>) => UseQueryResult<IPageable<T>, Error>)
    | ((id: string) => UseQueryResult<IPageable<T>, Error>)
    | ((params: Params<T>) => Promise<IPageable<T>>)
    | ((id: string) => Promise<T>);
  renderActions?: (row: T) => React.ReactElement[];
  shadeRow?: (row: T) => boolean;
  shadeBg?: string | ((row: T) => string | undefined);
  shadeColor?: string;
  checkboxSelect?: boolean;
  onRowSelect?: (row: T, checked: boolean) => void;
  isRowSelected?: (row: T) => boolean;
  sortableFields?: (keyof T)[];
  highlightDiff?: boolean;
  diffScope?: "adjacent" | "all";
  onRowClick?: (row: T) => void;
  /** Sort applied on first render, before the user clicks a column header. */
  initialSort?: ISort<T>;
}

function SearchableTable<T>({
  fields,
  filters,
  actions = [],
  fetchFunc,
  renderActions,
  shadeRow,
  shadeBg,
  shadeColor,
  checkboxSelect,
  onRowSelect,
  isRowSelected,
  sortableFields,
  highlightDiff = false, // <-- explicitly destructure
  diffScope = "adjacent",
  onRowClick,
  initialSort,
  ...props
}: ISearchableTableProps<T>) {
  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <PaginatedTable<T>
        fields={fields}
        filters={filters}
        actions={actions}
        fetchFunc={fetchFunc}
        renderActions={renderActions}
        shadeRow={shadeRow}
        shadeBg={shadeBg}
        shadeColor={shadeColor}
        checkboxSelect={checkboxSelect}
        onRowSelect={onRowSelect}
        isRowSelected={isRowSelected}
        sortableFields={sortableFields}
        highlightDiff={highlightDiff} // <-- pass directly
        diffScope={diffScope}
        onRowClick={onRowClick}
        initialSort={initialSort}
      />
    </Box>
  );
}

export default SearchableTable;
